/**
 * 来場者認証サービス
 * visitor-login / visitor-logout 相当の処理を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import {
  validateRequiredString,
  validateVisitorLoginUsername,
  validateVisitorPassword,
  validateVisitorUsername,
} from '../../../shared/utils/validation';

/** visitor-login RPC名 */
const VISITOR_LOGIN_RPC_NAME = 'visitor_login';

/** visitor-register RPC名 */
const VISITOR_REGISTER_RPC_NAME = 'visitor_register';

/** visitor-get-active-terms RPC名 */
const VISITOR_GET_ACTIVE_TERMS_RPC_NAME = 'visitor_get_active_terms';

/** visitor-get-terms-consent-status RPC名 */
const VISITOR_GET_TERMS_CONSENT_STATUS_RPC_NAME = 'visitor_get_terms_consent_status';

/** visitor-accept-active-terms RPC名 */
const VISITOR_ACCEPT_ACTIVE_TERMS_RPC_NAME = 'visitor_accept_active_terms';

/** visitor-logout RPC名 */
const VISITOR_LOGOUT_RPC_NAME = 'visitor_logout';

/** visitor-restore-session RPC名 */
const VISITOR_RESTORE_SESSION_RPC_NAME = 'visitor_restore_session';

/** 認証失敗エラーコード */
export const VISITOR_LOGIN_INVALID_CREDENTIALS_CODE = 'VISITOR_LOGIN_INVALID_CREDENTIALS';

/** 入力不正エラーコード */
export const VISITOR_LOGIN_INVALID_INPUT_CODE = 'VISITOR_LOGIN_INVALID_INPUT';

/** ログイン試行回数超過エラーコード */
export const VISITOR_LOGIN_RATE_LIMIT_CODE = 'VISITOR_LOGIN_RATE_LIMIT';

/** ログイン失敗エラーコード */
export const VISITOR_LOGIN_FAILED_CODE = 'VISITOR_LOGIN_FAILED';

/** 新規登録ユーザ名重複エラーコード */
export const VISITOR_REGISTER_USERNAME_TAKEN_CODE = 'VISITOR_REGISTER_USERNAME_TAKEN';

/** 新規登録入力不正エラーコード */
export const VISITOR_REGISTER_INVALID_INPUT_CODE = 'VISITOR_REGISTER_INVALID_INPUT';

/** 新規登録試行回数超過エラーコード */
export const VISITOR_REGISTER_RATE_LIMIT_CODE = 'VISITOR_REGISTER_RATE_LIMIT';

/** 新規登録失敗エラーコード */
export const VISITOR_REGISTER_FAILED_CODE = 'VISITOR_REGISTER_FAILED';

/** 利用規約未同意エラーコード */
export const VISITOR_REGISTER_TERMS_NOT_ACCEPTED_CODE = 'VISITOR_REGISTER_TERMS_NOT_ACCEPTED';

/** 利用規約版更新エラーコード */
export const VISITOR_REGISTER_TERMS_VERSION_OUTDATED_CODE = 'VISITOR_REGISTER_TERMS_VERSION_OUTDATED';

/** 利用規約取得失敗エラーコード */
export const VISITOR_TERMS_FETCH_FAILED_CODE = 'VISITOR_TERMS_FETCH_FAILED';

/** 有効な利用規約未設定エラーコード */
export const VISITOR_TERMS_UNAVAILABLE_CODE = 'VISITOR_TERMS_UNAVAILABLE';

/** 利用規約同意状況取得失敗エラーコード */
export const VISITOR_TERMS_RECONSENT_STATUS_FAILED_CODE =
  'VISITOR_TERMS_RECONSENT_STATUS_FAILED';

/** 利用規約再同意失敗エラーコード */
export const VISITOR_TERMS_RECONSENT_ACCEPT_FAILED_CODE =
  'VISITOR_TERMS_RECONSENT_ACCEPT_FAILED';

/** 利用規約再同意用セッション不正エラーコード */
export const VISITOR_TERMS_RECONSENT_SESSION_INVALID_CODE =
  'VISITOR_TERMS_RECONSENT_SESSION_INVALID';

/** 利用規約再同意の明示同意不足エラーコード */
export const VISITOR_TERMS_RECONSENT_NOT_ACCEPTED_CODE =
  'VISITOR_TERMS_RECONSENT_NOT_ACCEPTED';

/** 利用規約再同意中の版更新エラーコード */
export const VISITOR_TERMS_RECONSENT_VERSION_OUTDATED_CODE =
  'VISITOR_TERMS_RECONSENT_VERSION_OUTDATED';

/** ログアウト失敗エラーコード */
export const VISITOR_LOGOUT_FAILED_CODE = 'VISITOR_LOGOUT_FAILED';

/** セッション未検出エラーコード */
export const VISITOR_LOGOUT_SESSION_NOT_FOUND_CODE = 'VISITOR_LOGOUT_SESSION_NOT_FOUND';

/** セッション復元失敗エラーコード */
export const VISITOR_RESTORE_SESSION_FAILED_CODE = 'VISITOR_RESTORE_SESSION_FAILED';

/**
 * RPCエラーが認証失敗か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} 認証失敗の場合true
 */
const isInvalidCredentialsError = (rpcError) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes('INVALID_CREDENTIALS')
    || rpcError.message.includes('VISITOR_INACTIVE');
};

/**
 * RPCエラーがログインレート制限超過か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} レート制限超過の場合true
 */
const isLoginRateLimitError = (rpcError) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes('RATE_LIMIT_EXCEEDED');
};

/**
 * RPCエラーがユーザ名重複か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} ユーザ名重複の場合true
 */
const isUsernameTakenError = (rpcError) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes('USERNAME_TAKEN');
};

/**
 * RPCエラーが入力不正か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} 入力不正の場合true
 */
const isRegisterInvalidInputError = (rpcError) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes('INVALID_INPUT');
};

/**
 * RPCエラーが利用規約未同意か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} 利用規約未同意の場合true
 */
const isTermsNotAcceptedError = (rpcError) => {
  return Boolean(rpcError?.message?.includes('TERMS_NOT_ACCEPTED'));
};

/**
 * RPCエラーが利用規約版更新か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} 利用規約版更新の場合true
 */
const isTermsVersionOutdatedError = (rpcError) => {
  return Boolean(rpcError?.message?.includes('TERMS_VERSION_OUTDATED'));
};

/**
 * RPCエラーが来場者セッション不正か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} セッション不正の場合true
 */
const isVisitorSessionError = (rpcError) => {
  return Boolean(
    rpcError?.message?.includes('INVALID_SESSION_TOKEN')
    || rpcError?.message?.includes('SESSION_NOT_FOUND'),
  );
};

/**
 * RPCエラーが有効な利用規約未設定か判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} 有効規約未設定の場合true
 */
const isTermsUnavailableError = (rpcError) => {
  return Boolean(rpcError?.message?.includes('TERMS_UNAVAILABLE'));
};

/**
 * RPC結果から単一行データを取り出す
 * @template T
 * @param {T[] | T | null} rpcData - RPC戻り値
 * @returns {T | null} 単一行データ
 */
const pickSingleRpcRow = (rpcData) => {
  if (Array.isArray(rpcData)) {
    return rpcData[0] ?? null;
  }

  return rpcData ?? null;
};

/**
 * 有効な利用規約のRPC結果を検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{ termsId: string, version: string, title: string, content: string, effectiveAt: string }} 利用規約
 * @throws {AppError} 有効な利用規約がない、またはレスポンス不正の場合
 */
const validateActiveTermsRow = (rpcRow) => {
  if (!rpcRow) {
    throw new AppError('現在有効な利用規約が設定されていないため、新規登録できません。', {
      code: VISITOR_TERMS_UNAVAILABLE_CODE,
      statusCode: 503,
    });
  }

  /** 利用規約ID */
  const termsId = rpcRow.terms_id;
  /** 利用規約版 */
  const version = rpcRow.version;
  /** 利用規約タイトル */
  const title = rpcRow.title;
  /** 利用規約本文 */
  const content = rpcRow.content;
  /** 利用規約施行日時 */
  const effectiveAt = rpcRow.effective_at;

  if (!termsId || !version || !title || !content || !effectiveAt) {
    throw new AppError('利用規約のレスポンス形式が不正です。', {
      code: VISITOR_TERMS_FETCH_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  return {
    termsId,
    version,
    title,
    content,
    effectiveAt,
  };
};

/**
 * 利用規約同意状況のRPC結果を検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{
 * requiresAcceptance: boolean,
 * terms: { termsId: string, version: string, title: string, content: string, effectiveAt: string },
 * }} 利用規約同意状況
 * @throws {AppError} レスポンス不正の場合
 */
const validateTermsConsentStatusRow = (rpcRow) => {
  if (typeof rpcRow?.requires_acceptance !== 'boolean') {
    throw new AppError('利用規約同意状況のレスポンス形式が不正です。', {
      code: VISITOR_TERMS_RECONSENT_STATUS_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  return {
    requiresAcceptance: rpcRow.requires_acceptance,
    terms: validateActiveTermsRow(rpcRow),
  };
};

/**
 * 利用規約再同意のRPC結果を検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{ accepted: true, termsId: string, version: string, acceptedAt: string }} 再同意結果
 * @throws {AppError} レスポンス不正の場合
 */
const validateTermsAcceptanceRow = (rpcRow) => {
  /** 同意結果 */
  const accepted = rpcRow?.accepted;
  /** 利用規約ID */
  const termsId = rpcRow?.terms_id;
  /** 利用規約版 */
  const version = rpcRow?.version;
  /** 同意日時 */
  const acceptedAt = rpcRow?.accepted_at;

  if (accepted !== true || !termsId || !version || !acceptedAt) {
    throw new AppError('利用規約再同意のレスポンス形式が不正です。', {
      code: VISITOR_TERMS_RECONSENT_ACCEPT_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  return {
    accepted: true,
    termsId,
    version,
    acceptedAt,
  };
};

/**
 * 現在有効な利用規約を取得する
 * @returns {Promise<{ termsId: string, version: string, title: string, content: string, effectiveAt: string }>} 利用規約
 * @throws {AppError} 取得に失敗した場合
 */
export const getActiveTerms = async () => {
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_GET_ACTIVE_TERMS_RPC_NAME);

  if (error) {
    logError('visitor-get-active-terms RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('利用規約の取得に失敗しました。通信環境を確認して再度お試しください。', {
      code: VISITOR_TERMS_FETCH_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  return validateActiveTermsRow(pickSingleRpcRow(data));
};

/**
 * 有効な利用規約への本人の同意状況をDB内で判定する
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<{
 * requiresAcceptance: boolean,
 * terms: { termsId: string, version: string, title: string, content: string, effectiveAt: string },
 * }>} 利用規約同意状況
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorGetTermsConsentStatus = async (sessionToken) => {
  /** セッショントークン検証結果 */
  const validationResult = validateRequiredString(sessionToken, 'セッショントークン');

  if (!validationResult.isValid) {
    throw new AppError(validationResult.message, {
      code: VISITOR_TERMS_RECONSENT_SESSION_INVALID_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(
    VISITOR_GET_TERMS_CONSENT_STATUS_RPC_NAME,
    {
      p_session_token: sessionToken.trim(),
    },
  );

  if (error) {
    if (isVisitorSessionError(error)) {
      throw new AppError('セッションを確認できませんでした。ログアウトして再度ログインしてください。', {
        code: VISITOR_TERMS_RECONSENT_SESSION_INVALID_CODE,
        statusCode: 401,
        details: { message: error.message },
      });
    }

    if (isTermsUnavailableError(error)) {
      throw new AppError('現在有効な利用規約が設定されていません。', {
        code: VISITOR_TERMS_UNAVAILABLE_CODE,
        statusCode: 503,
        details: { message: error.message },
      });
    }

    logError('visitor-get-terms-consent-status RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('利用規約の同意状況を確認できませんでした。通信環境を確認して再度お試しください。', {
      code: VISITOR_TERMS_RECONSENT_STATUS_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  return validateTermsConsentStatusRow(pickSingleRpcRow(data));
};

/**
 * 現在有効な利用規約への再同意を記録する
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} termsVersion - 画面へ表示した利用規約版
 * @param {boolean} termsAccepted - 明示同意有無
 * @returns {Promise<{ accepted: true, termsId: string, version: string, acceptedAt: string }>} 再同意結果
 * @throws {AppError} 再同意に失敗した場合
 */
export const visitorAcceptActiveTerms = async (
  sessionToken,
  termsVersion,
  termsAccepted,
) => {
  /** セッショントークン検証結果 */
  const sessionValidationResult = validateRequiredString(
    sessionToken,
    'セッショントークン',
  );

  if (!sessionValidationResult.isValid) {
    throw new AppError(sessionValidationResult.message, {
      code: VISITOR_TERMS_RECONSENT_SESSION_INVALID_CODE,
      statusCode: 400,
    });
  }

  /** 利用規約版検証結果 */
  const termsVersionValidationResult = validateRequiredString(termsVersion, '利用規約版');

  if (!termsVersionValidationResult.isValid) {
    throw new AppError(termsVersionValidationResult.message, {
      code: VISITOR_TERMS_RECONSENT_ACCEPT_FAILED_CODE,
      statusCode: 400,
    });
  }

  if (termsAccepted !== true) {
    throw new AppError('利用規約への同意が必要です。', {
      code: VISITOR_TERMS_RECONSENT_NOT_ACCEPTED_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();
  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(
    VISITOR_ACCEPT_ACTIVE_TERMS_RPC_NAME,
    {
      p_session_token: sessionToken.trim(),
      p_terms_version: termsVersion.trim(),
      p_terms_accepted: termsAccepted,
    },
  );

  if (error) {
    if (isVisitorSessionError(error)) {
      throw new AppError('セッションを確認できませんでした。ログアウトして再度ログインしてください。', {
        code: VISITOR_TERMS_RECONSENT_SESSION_INVALID_CODE,
        statusCode: 401,
        details: { message: error.message },
      });
    }

    if (isTermsNotAcceptedError(error)) {
      throw new AppError('利用規約への同意が必要です。', {
        code: VISITOR_TERMS_RECONSENT_NOT_ACCEPTED_CODE,
        statusCode: 400,
        details: { message: error.message },
      });
    }

    if (isTermsVersionOutdatedError(error)) {
      throw new AppError('利用規約が更新されました。最新の内容を確認し、もう一度同意してください。', {
        code: VISITOR_TERMS_RECONSENT_VERSION_OUTDATED_CODE,
        statusCode: 409,
        details: { message: error.message },
      });
    }

    if (isTermsUnavailableError(error)) {
      throw new AppError('現在有効な利用規約が設定されていません。', {
        code: VISITOR_TERMS_UNAVAILABLE_CODE,
        statusCode: 503,
        details: { message: error.message },
      });
    }

    logError('visitor-accept-active-terms RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('利用規約への同意を記録できませんでした。通信環境を確認して再度お試しください。', {
      code: VISITOR_TERMS_RECONSENT_ACCEPT_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  return validateTermsAcceptanceRow(pickSingleRpcRow(data));
};

/**
 * visitor-login 結果を検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{ visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string }} ログイン結果
 * @throws {AppError} レスポンス不正の場合
 */
const validateVisitorLoginRow = (rpcRow) => {
  /** visitorId */
  const visitorId = rpcRow?.visitor_id;

  /** nickname */
  const nickname = rpcRow?.nickname;

  /** currentPoint */
  const currentPoint = rpcRow?.current_point;

  /** maxReachedPoint */
  const maxReachedPoint = Number(rpcRow?.max_reached_point ?? 0);

  /** sessionToken */
  const sessionToken = rpcRow?.session_token;

  /** sessionExpiresAt */
  const sessionExpiresAt = rpcRow?.session_expires_at;

  if (
    !visitorId
    || !nickname
    || typeof currentPoint !== 'number'
    || Number.isNaN(maxReachedPoint)
    || !sessionToken
    || !sessionExpiresAt
  ) {
    throw new AppError('visitor-login のレスポンス形式が不正です。', {
      code: VISITOR_LOGIN_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  return {
    visitorId,
    nickname,
    currentPoint,
    maxReachedPoint,
    sessionToken,
    sessionExpiresAt,
  };
};

/**
 * visitor-login を実行する
 * @param {string} username - ログイン対象ユーザ名
 * @param {string} password - ログイン対象パスワード
 * @returns {Promise<{ visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string }>} ログイン結果
 * @throws {AppError} ログインに失敗した場合
 */
export const visitorLogin = async (username, password) => {
  /** ユーザ名検証結果 */
  const usernameValidationResult = validateVisitorLoginUsername(username);

  if (!usernameValidationResult.isValid) {
    throw new AppError(usernameValidationResult.message, {
      code: VISITOR_LOGIN_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** パスワード検証結果 */
  const passwordValidationResult = validateVisitorPassword(password);

  if (!passwordValidationResult.isValid) {
    throw new AppError(passwordValidationResult.message, {
      code: VISITOR_LOGIN_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_LOGIN_RPC_NAME, {
    p_username: username.trim(),
    p_password: String(password),
  });

  if (error) {
    if (isInvalidCredentialsError(error)) {
      throw new AppError('ユーザ名またはパスワードが正しくありません。', {
        code: VISITOR_LOGIN_INVALID_CREDENTIALS_CODE,
        statusCode: 401,
        details: { message: error.message },
      });
    }

    if (isLoginRateLimitError(error)) {
      throw new AppError('ログイン試行回数が上限に達しました。1分ほど待ってから再度お試しください。', {
        code: VISITOR_LOGIN_RATE_LIMIT_CODE,
        statusCode: 429,
        details: { message: error.message },
      });
    }

    logError('visitor-login RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      username,
    });

    throw new AppError('visitor-login の実行に失敗しました。', {
      code: VISITOR_LOGIN_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error.message,
      },
    });
  }

  /** 単一行のRPC結果 */
  const rpcRow = pickSingleRpcRow(data);

  // 失敗した試行を試行回数に残すため、DBはパスワード違いと存在しないユーザ名を例外ではなく0行で返す。
  // 例外で返す旧DB向けの判定（isInvalidCredentialsError）も、反映順のずれに備えて残す。
  if (!rpcRow) {
    throw new AppError('ユーザ名またはパスワードが正しくありません。', {
      code: VISITOR_LOGIN_INVALID_CREDENTIALS_CODE,
      statusCode: 401,
    });
  }

  return validateVisitorLoginRow(rpcRow);
};

/**
 * visitor-register を実行する
 * @param {string} username - 登録するユーザ名
 * @param {string} password - 登録するパスワード
 * @param {string} termsVersion - 同意する利用規約版
 * @param {boolean} termsAccepted - 利用規約同意有無
 * @returns {Promise<{ visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string }>} 登録結果
 * @throws {AppError} 登録に失敗した場合
 */
export const visitorRegister = async (username, password, termsVersion, termsAccepted) => {
  /** ユーザ名検証結果 */
  const usernameValidationResult = validateVisitorUsername(username);

  if (!usernameValidationResult.isValid) {
    throw new AppError(usernameValidationResult.message, {
      code: VISITOR_REGISTER_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** パスワード検証結果 */
  const passwordValidationResult = validateVisitorPassword(password);

  if (!passwordValidationResult.isValid) {
    throw new AppError(passwordValidationResult.message, {
      code: VISITOR_REGISTER_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** 利用規約版検証結果 */
  const termsVersionValidationResult = validateRequiredString(termsVersion, '利用規約版');

  if (!termsVersionValidationResult.isValid) {
    throw new AppError(termsVersionValidationResult.message, {
      code: VISITOR_REGISTER_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  if (termsAccepted !== true) {
    throw new AppError('利用規約への同意が必要です。', {
      code: VISITOR_REGISTER_TERMS_NOT_ACCEPTED_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_REGISTER_RPC_NAME, {
    p_username: username.trim(),
    p_password: String(password),
    p_terms_version: termsVersion.trim(),
    p_terms_accepted: termsAccepted,
  });

  if (error) {
    if (isUsernameTakenError(error)) {
      throw new AppError('このユーザ名はすでに使われています。別の名前を入力してください。', {
        code: VISITOR_REGISTER_USERNAME_TAKEN_CODE,
        statusCode: 409,
        details: { message: error.message },
      });
    }

    if (isRegisterInvalidInputError(error)) {
      throw new AppError('入力内容に誤りがあります。', {
        code: VISITOR_REGISTER_INVALID_INPUT_CODE,
        statusCode: 400,
        details: { message: error.message },
      });
    }

    if (isTermsNotAcceptedError(error)) {
      throw new AppError('利用規約への同意が必要です。', {
        code: VISITOR_REGISTER_TERMS_NOT_ACCEPTED_CODE,
        statusCode: 400,
        details: { message: error.message },
      });
    }

    if (isTermsVersionOutdatedError(error)) {
      throw new AppError('利用規約が更新されました。内容を確認し、もう一度同意してください。', {
        code: VISITOR_REGISTER_TERMS_VERSION_OUTDATED_CODE,
        statusCode: 409,
        details: { message: error.message },
      });
    }

    if (isLoginRateLimitError(error)) {
      throw new AppError('新規登録試行回数が上限に達しました。1分ほど待ってから再度お試しください。', {
        code: VISITOR_REGISTER_RATE_LIMIT_CODE,
        statusCode: 429,
        details: { message: error.message },
      });
    }

    logError('visitor-register RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      username,
    });

    throw new AppError('visitor-register の実行に失敗しました。', {
      code: VISITOR_REGISTER_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error.message,
      },
    });
  }

  /** 単一行のRPC結果 */
  const rpcRow = pickSingleRpcRow(data);
  return validateVisitorLoginRow(rpcRow);
};

/**
 * visitor-logout 結果を検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{ revoked: boolean, visitorId: string | null }} ログアウト結果
 */
const validateVisitorLogoutRow = (rpcRow) => {
  /** revoked */
  const revoked = Boolean(rpcRow?.revoked);

  /** visitorId */
  const visitorId = rpcRow?.visitor_id ?? null;

  return {
    revoked,
    visitorId,
  };
};

/**
 * visitor-restore-session 結果を検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{
 * isValid: boolean,
 * isLost: boolean,
 * visitorId: string | null,
 * nickname: string,
 * currentPoint: number,
 * maxReachedPoint: number,
 * sessionExpiresAt: string,
 * }} 復元結果
 * @throws {AppError} レスポンス不正の場合
 */
const validateVisitorRestoreRow = (rpcRow) => {
  /** isValid */
  const isValid = Boolean(rpcRow?.session_is_valid);

  /** isLost */
  const isLost = Boolean(rpcRow?.session_lost);

  /** visitorId */
  const visitorId = rpcRow?.visitor_id ?? null;

  /** nickname */
  const nickname = rpcRow?.nickname ?? '';

  /** currentPoint */
  const currentPoint = Number(rpcRow?.current_point ?? 0);

  /** maxReachedPoint */
  const maxReachedPoint = Number(rpcRow?.max_reached_point ?? 0);

  /** sessionExpiresAt */
  const sessionExpiresAt = rpcRow?.session_expires_at ?? '';

  if (!isValid) {
    return {
      isValid: false,
      isLost,
      visitorId,
      nickname: '',
      currentPoint: 0,
      maxReachedPoint: 0,
      sessionExpiresAt: '',
    };
  }

  if (!visitorId || !nickname || !sessionExpiresAt || Number.isNaN(currentPoint) || Number.isNaN(maxReachedPoint)) {
    throw new AppError('visitor-restore-session のレスポンス形式が不正です。', {
      code: VISITOR_RESTORE_SESSION_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  return {
    isValid: true,
    isLost,
    visitorId,
    nickname,
    currentPoint,
    maxReachedPoint,
    sessionExpiresAt,
  };
};

/**
 * visitor-logout を実行する
 * @param {string} sessionToken - ログアウト対象セッショントークン
 * @returns {Promise<{ revoked: boolean, visitorId: string | null }>} ログアウト結果
 * @throws {AppError} ログアウトに失敗した場合
 */
export const visitorLogout = async (sessionToken) => {
  /** セッショントークン検証結果 */
  const validationResult = validateRequiredString(sessionToken, 'セッショントークン');

  if (!validationResult.isValid) {
    throw new AppError(validationResult.message, {
      code: VISITOR_LOGOUT_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_LOGOUT_RPC_NAME, {
    p_session_token: sessionToken,
  });

  if (error) {
    logError('visitor-logout RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('visitor-logout の実行に失敗しました。', {
      code: VISITOR_LOGOUT_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error.message,
      },
    });
  }

  /** 単一行のRPC結果 */
  const rpcRow = pickSingleRpcRow(data);

  /** ログアウト結果 */
  const logoutResult = validateVisitorLogoutRow(rpcRow);

  if (!logoutResult.revoked) {
    throw new AppError('有効なセッションが見つかりませんでした。', {
      code: VISITOR_LOGOUT_SESSION_NOT_FOUND_CODE,
      statusCode: 404,
    });
  }

  return logoutResult;
};

/**
 * visitor-restore-session を実行する
 * @param {string} sessionToken - 復元対象セッショントークン
 * @returns {Promise<{
 * isValid: boolean,
 * isLost: boolean,
 * visitorId: string | null,
 * nickname: string,
 * currentPoint: number,
 * maxReachedPoint: number,
 * sessionExpiresAt: string,
 * }>} 復元結果
 * @throws {AppError} RPC失敗時
 */
export const visitorRestoreSession = async (sessionToken) => {
  /** セッショントークン検証結果 */
  const validationResult = validateRequiredString(sessionToken, 'セッショントークン');

  if (!validationResult.isValid) {
    throw new AppError(validationResult.message, {
      code: VISITOR_RESTORE_SESSION_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(VISITOR_RESTORE_SESSION_RPC_NAME, {
    p_session_token: sessionToken,
  });

  if (error) {
    logError('visitor-restore-session RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('visitor-restore-session の実行に失敗しました。', {
      code: VISITOR_RESTORE_SESSION_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error.message,
      },
    });
  }

  /** 単一行のRPC結果 */
  const rpcRow = pickSingleRpcRow(data);
  return validateVisitorRestoreRow(rpcRow);
};
